/**
 * Local error logging system to replace Sentry functionality
 * Provides structured error logging with context tracking
 */

export interface ErrorContext {
  component?: string;
  operation?: string;
  userId?: string;
  meetingId?: string;
  participantId?: string;
  metadata?: Record<string, any>;
  silent?: boolean; // Don't log to console
}

export interface UserInteraction {
  action: string;
  component: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface PerformanceMetric {
  component: string;
  operation: string;
  duration: number;
  success: boolean;
  metadata?: Record<string, any>;
}

class LocalLogger {
  private interactions: UserInteraction[] = [];
  private sessionStart = Date.now();
  private maxInteractions = 100;

  // Error logging
  captureError(error: Error, context?: ErrorContext) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Only log to console if not marked as silent
    if (!context?.silent) {
      console.error('Error captured:', errorData);
    }

    // Store in localStorage for debugging
    this.storeError(errorData);
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
    const messageData = {
      message,
      level,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    console[level === 'warning' ? 'warn' : level === 'error' ? 'error' : 'log'](
      'Message captured:',
      messageData
    );
  }

  // User tracking
  setUser(user: { id: string; email?: string; name?: string }) {
    // console.log('User context set:', user);
    sessionStorage.setItem('user_context', JSON.stringify(user));
  }

  setContext(key: string, context: Record<string, any>) {
    // console.log(`Context set [${key}]:`, context);
    sessionStorage.setItem(`context_${key}`, JSON.stringify(context));
  }

  // Breadcrumbs/interactions
  addBreadcrumb(message: string, category: string, level: 'info' | 'warning' | 'error' = 'info') {
    const interaction: UserInteraction = {
      action: message,
      component: category,
      timestamp: Date.now(),
      metadata: { level }
    };

    this.interactions.push(interaction);

    // Keep only recent interactions
    if (this.interactions.length > this.maxInteractions) {
      this.interactions.shift();
    }

    // console.log(`Breadcrumb [${category}]:`, message);
  }

  // Performance tracking
  trackPerformance(metric: PerformanceMetric) {
    console.log('Performance metric:', metric);

    // Log slow operations
    if (metric.duration > 1000) {
      console.warn(`Slow operation detected: ${metric.component}.${metric.operation} took ${metric.duration}ms`);
    }
  }

  // Business event tracking
  trackEvent(event: string, data: Record<string, any>) {
    const eventData = {
      event,
      data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    console.log('Business event:', eventData);
  }

  // Session management
  clearSession() {
    this.interactions = [];
    sessionStorage.removeItem('user_context');
    // Clear context items
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('context_')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  getAnalyticsSummary() {
    return {
      sessionDuration: Date.now() - this.sessionStart,
      interactionCount: this.interactions.length,
      recentInteractions: this.interactions.slice(-10),
    };
  }

  // Helper methods
  private storeError(errorData: any) {
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors.push(errorData);

      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.shift();
      }

      localStorage.setItem('app_errors', JSON.stringify(errors));
    } catch (e) {
      console.warn('Failed to store error in localStorage:', e);
    }
  }
}

export const localLogger = new LocalLogger();
